import { useLayoutEffect, useRef } from "react";
import { useViewport } from "src/contexts";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";
import { DraggableImageWrapper } from "../atoms/DraggableImageWrapper";
import { LotSaleStatusLabel } from "../atoms/LotSaleStatusLabel";

export enum LotMapSalesStatus {
  Available = "AVAILABLE",
  Reserved = "RESERVED",
  ConstructionStarted = "CONSTRUCTION_STARTED",
  MoveInReady = "MOVE_IN_READY",
  ModelHome = "MODEL_HOME",
  ExistingResidential = "EXISTING_RESIDENTIAL",
}

export type Lot = {
  id: string; // structure id, e.g., "p:2"
  state: LotMapSalesStatus;
};

type LotMapProps = {
  // Raw SVG markup for the lot map
  svg: string;
  // Array of lot objects with id and state
  lots: Lot[];
  // Lot ID to show hover state
  hoveredLotId?: string;
  // Lot ID to show hover state (both hoveredLotId and selectedLotId trigger hover)
  selectedLotId?: string;
  // Callback when a lot is clicked
  onClick?: (lotId: string) => void;
  // Callback when a lot is hovered (null when mouse leaves)
  onHover?: (lotId: string | null) => void;
};

// Which SVG elements a lot or border group can be drawn with. Varies by map.
const SHAPE_SELECTOR = "polygon, rect, polyline, path";

const STATE_TO_CLASS: Record<LotMapSalesStatus, string> = {
  AVAILABLE: "green",
  CONSTRUCTION_STARTED: "blue",
  MOVE_IN_READY: "purple",
  RESERVED: "red",
  MODEL_HOME: "orange",
  EXISTING_RESIDENTIAL: "white",
};

export function LotMap(props: LotMapProps) {
  const { svg, lots, hoveredLotId, selectedLotId, onClick, onHover } = props;
  const tid = useTestIds(props, "lotMap");
  const { isMobile } = useViewport();
  // stores the reference to the `div` containing the `svg` element
  const svgContainerRef = useRef<HTMLDivElement>(null);

  // Attach handlers and apply initial lot states
  useLayoutEffect(() => {
    if (svgContainerRef.current) {
      const svgEl = svgContainerRef.current.querySelector("svg");
      if (svgEl) {
        try {
          applyLotStates(svgEl, lots);
          attachEventListeners(svgEl, lots, onClick, onHover);
        } catch (error) {
          console.error("Lot map render error", error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svg, lots]);

  // Effect for hover and selected states
  useLayoutEffect(() => {
    if (svgContainerRef.current) {
      const svgEl = svgContainerRef.current.querySelector("svg");
      if (svgEl) {
        try {
          applyHoverStates(svgEl, hoveredLotId, selectedLotId);
        } catch (error) {
          console.error("Lot map render error", error);
        }
      }
    }
  }, [hoveredLotId, selectedLotId, svg]);

  return (
    <>
      <DraggableImageWrapper draggable={true} initialScale={1} contentStyle={Css.h100.w100.$}>
        {/* Lot map content */}
        <div
          css={{
            // Since creating these files in a "perfect" ratio for a responsive desktop and mobile is
            // a pain, we'll just size as big as it wants and wrap it in a DraggableImageWrapper to allow
            // for panning and zooming since that works for desktop too. woohoo!
            backgroundColor: "#e4ece6", // The "grass" color of our community maps
            width: "100%",
            height: "100%",
            svg: {
              height: "100%",
              width: "100%",
            },
          }}
          ref={svgContainerRef}
          dangerouslySetInnerHTML={{ __html: svg }}
          {...tid}
        />
      </DraggableImageWrapper>
      <LotMapLegend isMobile={isMobile} />
    </>
  );
}

function LotMapLegend({ isMobile }: { isMobile: boolean }) {
  const orderedStatuses: LotMapSalesStatus[] = [
    LotMapSalesStatus.Available,
    LotMapSalesStatus.ConstructionStarted,
    LotMapSalesStatus.MoveInReady,
    LotMapSalesStatus.Reserved,
    LotMapSalesStatus.ModelHome,
    LotMapSalesStatus.ExistingResidential,
  ];

  return (
    <div css={Css.bgColor("#F1F1F1").bt.bb.bcGray16.df.jcc.aic.w100.hPx(32).$}>
      <div css={Css.df.jcsb.gap3.p2.oa.ifMobile.gap2.$}>
        {Object.values(orderedStatuses).map((status) => (
          <LotSaleStatusLabel key={status} size={isMobile ? "small" : "large"} status={status} />
        ))}
      </div>
    </div>
  );
}

// SVG Helpers -------

/**
 * Get all lot groups from the SVG, excluding labels and borders
 */
function getLotGroups(svg: SVGSVGElement): SVGGElement[] {
  return Array.from(svg.querySelectorAll<SVGGElement>(`g[id^="p:"]`)).filter(
    (group) => !group.id.includes("Label") && !group.id.includes("Border"),
  );
}

/**
 * Apply state-based CSS classes to lot groups and their corresponding label groups
 */
function applyLotStates(svg: SVGSVGElement, lots: Lot[]) {
  // We ignore anything that doesn't start with "p:" since we don't own those lots
  // and their already styled uniquely in the file
  const allLabelGroups = Array.from(svg.querySelectorAll<SVGGElement>(`g[id^="p:"][id$="Label"]`));
  // Before applying states, hide all labels by default. Anything not passed into the lots array will
  // remain hidden
  allLabelGroups.forEach((labelGroup) => {
    // Make labels non-interactive so events pass through to underlying lots
    // easier than reapplying event listeners to them.
    labelGroup.style.pointerEvents = "none";
    const labelRects = labelGroup.querySelectorAll("rect");
    const textElements = labelGroup.querySelectorAll("text");
    updateElementsClass(labelRects, "hide", true);
    updateElementsClass(textElements, "hide", true);
  });

  // Apply state classes to lots
  for (const lot of lots) {
    // Find and update the corresponding label group
    const labelGroup = svg.querySelector<SVGGElement>(`g[id="${lot.id}Label"]`);
    if (labelGroup) {
      const labelRects = labelGroup.querySelectorAll("rect");
      const textElements = labelGroup.querySelectorAll("text");
      // Remove the hide that would've been applied above.
      // Not super effiicent but we're not at the scale for a second pass to matter
      updateElementsClass(labelRects, "hide", false);
      updateElementsClass(textElements, "hide", false);
      updateElementsClass(labelRects, STATE_TO_CLASS[lot.state], true);
    }
  }
}

/**
 * Attach click and hover event listeners to lot groups
 */
function attachEventListeners(
  svg: SVGSVGElement,
  lots: Lot[],
  onClick?: (lotId: string) => void,
  onHover?: (lotId: string | null) => void,
) {
  const allLotGroups = getLotGroups(svg);
  // Filter to only attach events to lot groups that are being shown on the page
  const lotGroups = allLotGroups.filter((group) => lots.some((lot) => lot.id === group.id));

  lotGroups.forEach((group) => {
    // Extract lot ID from group ID
    const lotId = group.id;

    // Remove existing listeners if they exist (stored as custom properties)
    const existingClickHandler = (group as any)._clickHandler;
    const existingMouseEnterHandler = (group as any)._mouseEnterHandler;
    const existingMouseLeaveHandler = (group as any)._mouseLeaveHandler;

    if (existingClickHandler) {
      group.removeEventListener("click", existingClickHandler);
      delete (group as any)._clickHandler;
    }
    if (existingMouseEnterHandler) {
      group.removeEventListener("mouseenter", existingMouseEnterHandler);
      delete (group as any)._mouseEnterHandler;
    }
    if (existingMouseLeaveHandler) {
      group.removeEventListener("mouseleave", existingMouseLeaveHandler);
      delete (group as any)._mouseLeaveHandler;
    }

    // Add click handler
    if (onClick) {
      const clickHandler = (e: MouseEvent) => {
        e.stopPropagation();
        onClick(lotId);
      };
      group.addEventListener("click", clickHandler);
      group.style.cursor = "pointer";
      // Store handler reference for cleanup
      (group as any)._clickHandler = clickHandler;
    } else {
      group.style.cursor = "";
    }

    // Add hover handlers
    if (onHover) {
      const mouseEnterHandler = () => {
        applyHoverState(group, true);
        onHover(lotId);
      };
      const mouseLeaveHandler = () => {
        applyHoverState(group, false);
        onHover(null);
      };
      group.addEventListener("mouseenter", mouseEnterHandler);
      group.addEventListener("mouseleave", mouseLeaveHandler);
      // Store handler references for cleanup
      (group as any)._mouseEnterHandler = mouseEnterHandler;
      (group as any)._mouseLeaveHandler = mouseLeaveHandler;
    }
  });
}

/**
 * Move a border group to the end of its parent to ensure it renders on top
 */
function moveBorderToEnd(borderGroup: SVGGElement | null) {
  if (borderGroup?.parentNode) {
    borderGroup.parentNode.appendChild(borderGroup);
  }
}

/**
 * Apply or remove classes to SVG elements
 */
function updateElementsClass(elements: NodeListOf<Element>, className: string, shouldAdd: boolean) {
  elements.forEach((element) => {
    if (element instanceof SVGElement) {
      if (shouldAdd) {
        element.classList.add(className);
      } else {
        element.classList.remove(className);
      }
    }
  });
}

/**
 * Apply or remove hover state styling to a lot
 */
function applyHoverState(lotGroup: SVGGElement, isHovered: boolean) {
  const children = lotGroup.querySelectorAll(SHAPE_SELECTOR);
  updateElementsClass(children, "hovered", isHovered);
}

/**
 * Apply or remove selected state styling to a lot (white fill with yellow border)
 */
function applySelectedState(lotGroup: SVGGElement, lotId: string, svg: SVGSVGElement, isSelected: boolean) {
  const children = lotGroup.querySelectorAll(SHAPE_SELECTOR);
  const borderGroup = svg.querySelector<SVGGElement>(`g[id="${lotId}Border"]`);
  const borderChildren = borderGroup?.querySelectorAll(SHAPE_SELECTOR);

  updateElementsClass(children, "selected", isSelected);
  if (borderChildren) {
    updateElementsClass(borderChildren, "yellow", isSelected);
    // Borders get moved around so the active one always renders on top and then moves back when
    // its done being highlighted to ensure it doesn't ruin the active state of a neighbor when hover is done
    if (isSelected) {
      moveBorderToEnd(borderGroup);
    }
  }
}

/**
 * Remove all hover and selected states from all lots
 */
function clearAllLotStates(svg: SVGSVGElement) {
  const allLotGroups = getLotGroups(svg);

  allLotGroups.forEach((group) => {
    const lotId = group.id;
    const children = group.querySelectorAll(SHAPE_SELECTOR);
    const borderGroup = svg.querySelector<SVGGElement>(`g[id="${lotId}Border"]`);

    updateElementsClass(children, "hovered", false);
    updateElementsClass(children, "selected", false);

    const borderChildren = borderGroup?.querySelectorAll(SHAPE_SELECTOR);
    if (borderChildren) {
      updateElementsClass(borderChildren, "yellow", false);
    }
  });
}

/**
 * Apply hover and selected state styling to lots based on hoveredLotId and selectedLotId props
 */
function applyHoverStates(svg: SVGSVGElement, hoveredLotId?: string, selectedLotId?: string) {
  clearAllLotStates(svg);

  // Apply selected state first (so border is positioned at the end)
  if (selectedLotId) {
    const lotGroup = svg.querySelector<SVGGElement>(`g[id="${selectedLotId}"]`);
    if (lotGroup) {
      applySelectedState(lotGroup, selectedLotId, svg, true);
    }
  }

  // Apply hover state
  if (hoveredLotId) {
    const lotGroup = svg.querySelector<SVGGElement>(`g[id="${hoveredLotId}"]`);
    if (lotGroup) {
      applyHoverState(lotGroup, true);
    }
  }
}
